import React from "react";
import {
  Grid,
  TextField,
  Button,
  IconButton,
  Box,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import {
  BRAND,
  primaryButtonSx,
  inputSx,
  mobileInputSx,
} from "../../../components/auth/authTheme";
import { MobileFormFooter } from "../profileViewHelpers";

const SectionCard = ({ title, icon, isMobile, action, children }) => (
  <Box
    sx={{
      bgcolor: "#fff",
      borderRadius: isMobile ? "14px" : 0,
      border: isMobile ? `1px solid ${BRAND.border}` : "none",
      p: isMobile ? 1.5 : 0,
      mb: isMobile ? 1.5 : 2,
      boxShadow: isMobile ? "0 1px 6px rgba(26, 43, 74, 0.04)" : "none",
    }}
  >
    {(title || action) && (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 0.75,
          mb: 1.25,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
          {icon && (
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "8px",
                bgcolor: BRAND.orangeLight,
                color: BRAND.orangeHover,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>
          )}
          {title && (
            <Typography sx={{ fontWeight: 700, color: BRAND.navy, fontSize: "0.9375rem" }}>
              {title}
            </Typography>
          )}
        </Box>
        {action}
      </Box>
    )}
    {children}
  </Box>
);

const validationSchema = Yup.object().shape({
  education: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().required("College name is required"),
      percentage: Yup.string().required("Percentage is required"),
      passingYear: Yup.string().required("Passing year is required"),
    })
  ),
});

const EducationDetailEditForm = ({ userDetails, onCancel, onSave }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const fieldSx = isMobile ? mobileInputSx : inputSx;
  const gridSpacing = isMobile ? 1.5 : 2;

  const initialValues = {
    education: userDetails?.profile_data?.education?.length
      ? userDetails.profile_data.education
      : [{ name: "", percentage: "", passingYear: "" }],
  };

  const handleSubmit = (values) => {
    onSave({
      id: userDetails.id,
      profile: { education: values.education },
    });
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
        <Form>
          <FieldArray name="education">
            {({ push, remove }) => (
              <Grid container spacing={gridSpacing} sx={{ ml: 0, width: "100%" }}>
                {values.education.map((edu, index) =>
                  isMobile ? (
                    <Grid item xs={12} key={index}>
                      <SectionCard
                        isMobile={isMobile}
                        title={`Education ${index + 1}`}
                        icon={<SchoolOutlinedIcon sx={{ fontSize: 16 }} />}
                        action={
                          values.education.length > 1 ? (
                            <IconButton
                              size="small"
                              onClick={() => remove(index)}
                              aria-label="Remove education"
                              sx={{ color: BRAND.orangeHover }}
                            >
                              <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                          ) : null
                        }
                      >
                        <Grid container spacing={gridSpacing}>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              name={`education[${index}].name`}
                              label="College Name"
                              value={edu.name}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={
                                touched.education?.[index]?.name &&
                                Boolean(errors.education?.[index]?.name)
                              }
                              helperText={
                                touched.education?.[index]?.name &&
                                errors.education?.[index]?.name
                              }
                              sx={fieldSx}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              name={`education[${index}].percentage`}
                              label="Percentage"
                              value={edu.percentage}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={
                                touched.education?.[index]?.percentage &&
                                Boolean(errors.education?.[index]?.percentage)
                              }
                              helperText={
                                touched.education?.[index]?.percentage &&
                                errors.education?.[index]?.percentage
                              }
                              sx={fieldSx}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              name={`education[${index}].passingYear`}
                              label="Passing Year"
                              value={edu.passingYear}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={
                                touched.education?.[index]?.passingYear &&
                                Boolean(errors.education?.[index]?.passingYear)
                              }
                              helperText={
                                touched.education?.[index]?.passingYear &&
                                errors.education?.[index]?.passingYear
                              }
                              sx={fieldSx}
                            />
                          </Grid>
                        </Grid>
                      </SectionCard>
                    </Grid>
                  ) : (
                    <React.Fragment key={index}>
                      <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            name={`education[${index}].name`}
                            label="College Name"
                            value={edu.name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.education?.[index]?.name &&
                              Boolean(errors.education?.[index]?.name)
                            }
                            helperText={
                              touched.education?.[index]?.name &&
                              errors.education?.[index]?.name
                            }
                            sx={fieldSx}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            fullWidth
                            name={`education[${index}].percentage`}
                            label="Percentage"
                            value={edu.percentage}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.education?.[index]?.percentage &&
                              Boolean(errors.education?.[index]?.percentage)
                            }
                            helperText={
                              touched.education?.[index]?.percentage &&
                              errors.education?.[index]?.percentage
                            }
                            sx={fieldSx}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            fullWidth
                            name={`education[${index}].passingYear`}
                            label="Passing Year"
                            value={edu.passingYear}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.education?.[index]?.passingYear &&
                              Boolean(errors.education?.[index]?.passingYear)
                            }
                            helperText={
                              touched.education?.[index]?.passingYear &&
                              errors.education?.[index]?.passingYear
                            }
                            sx={fieldSx}
                          />
                        </Grid>
                        <Grid item xs={12} sm={2} display="flex" alignItems="center">
                          <IconButton
                            onClick={() => remove(index)}
                            color="error"
                            disabled={values.education.length === 1}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Grid>
                    </React.Fragment>
                  )
                )}

                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    startIcon={isMobile ? <AddIcon /> : undefined}
                    onClick={() => push({ name: "", percentage: "", passingYear: "" })}
                    fullWidth={isMobile}
                    sx={
                      isMobile
                        ? {
                            minHeight: 44,
                            borderRadius: "12px",
                            textTransform: "none",
                            fontWeight: 600,
                            borderColor: BRAND.border,
                            color: BRAND.navy,
                            borderStyle: "dashed",
                          }
                        : undefined
                    }
                  >
                    Add More
                  </Button>
                </Grid>

                <Grid item xs={12}>
                  {isMobile ? (
                    <MobileFormFooter onCancel={onCancel} isSubmitting={isSubmitting} />
                  ) : (
                    <Box display="flex" justifyContent="flex-end" gap={2} mt={1}>
                      <Button variant="outlined" onClick={onCancel}>
                        Cancel
                      </Button>
                      <Button type="submit" variant="contained" sx={primaryButtonSx}>
                        Save
                      </Button>
                    </Box>
                  )}
                </Grid>
              </Grid>
            )}
          </FieldArray>
        </Form>
      )}
    </Formik>
  );
};

export default EducationDetailEditForm;
